/**
 * ai/decision.ts —— 拟人化 AI 决策器（PRD 模块 2）
 *
 * ⚠ 信息隔离硬约束（PRD 2.1 禁止项 / AC2.5）：
 *   本文件及其依赖（handStrength.ts / styles.ts）**不得** import：
 *     - ../equity        （赢率引擎）
 *     - ../preflopTable  （翻牌前离线精确表）
 *   也不得读取未开公共牌、其他玩家底牌。
 *   决策输入严格限定为 PRD 2.1 列举的 6 项：模糊牌力分档、位置、下注压力、
 *   本手历史动作、风格参数、情绪状态 + aiRNG 随机扰动。
 *   ai/__tests__/isolation.test.ts 用源码依赖扫描锁死这条约束。
 *
 * 拟人化四件套（PRD 2.1）：
 *   ① 风格参数带随机扰动区间   → jitter
 *   ② 情绪状态（连输大池后变松或变紧，3–10 手后回归） → EmotionState
 *   ③ 概率化犯错             → mistakeRate
 *   ④ 思考停顿 0.8–2.5s 随机  → thinkTimeMs()
 */

import type { Card } from '../cards';
import type { Action, LegalActions } from '../betting';
import type { Rng } from '../rng';
import { fuzzyStrength, TIER_NAMES_CN, type FuzzyStrength, type StrengthTier } from './handStrength';
import { getStyle, type Style, type StyleKey } from './styles';

/* ------------------------------------------------------------------ *
 * R12-2 翻牌后跟注再平衡旋钮（postflop-only；不进翻前分支、不触碰翻前阈值）
 * ------------------------------------------------------------------ *
 * 背景：R11 为修「弱/中等牌无脑跟大注」把面对下注的跟注压到 3–9%（over-nitty）。
 * 本轮按 PRD §4.2.2 目标行为带把中等牌/听牌跟注回调到「真人合理」并保持 5 风格分层，
 * 同时守住「弱牌无听牌高弃牌（S3）」「随下注尺度单调递减（S2<S1）」两条不回退线。
 *
 * 关键设计：翻后「纯跟黏性」与翻前 `callAppetite` **彻底解耦**——
 *   `callAppetite` 只在 decidePreflop 的 callAt 里用（保持翻前 VPIP/PFR 阶梯零风险，AC2.8），
 *   翻后各档跟注意愿改由下列 postflop 专用表驱动。
 * 纯跟黏性序（PRD §4.2.2）：station > lag > tag > rock > maniac（诈唬型纯跟最低、继续多经加注）。
 * 弱牌/边缘/听牌各自独立标定，互不牵连，便于按验收带精调。 */

/** 中等成手牌跟注意愿在 pressure=0 的截距（S1/S2 靶心：call≈截距−bite×pressure）。 */
const MED_CALL_1X: Readonly<Record<StyleKey, number>> = {
  rock: 0.90, tag: 0.87, lag: 0.96, station: 1.10, maniac: 0.40,
};
/** 中等牌每单位下注压力的跟注衰减（tight 风格更陡；保证 S2<S1 单调递减且落带）。 */
const MED_BITE: Readonly<Record<StyleKey, number>> = {
  rock: 0.32, tag: 0.27, lag: 0.26, station: 0.28, maniac: 0.09,
};
/** 成手牌（strong/monster）跟注乘子：对压力钝感，价值跟注。 */
const MADE_MULT: Readonly<Record<StyleKey, number>> = {
  rock: 1.0, tag: 1.0, lag: 1.05, station: 1.15, maniac: 0.85,
};
/** 听牌跟注黏性（配合 drawPressureFactor 的底池赔率衰减，服务 S4）。 */
const DRAW_CALL: Readonly<Record<StyleKey, number>> = {
  rock: 0.66, tag: 0.68, lag: 0.72, station: 0.88, maniac: 0.40,
};
/** 弱/边缘牌无听牌跟注乘子（守 S3 高弃牌，作用于 tierCallBase 的 marginal/weak）。 */
const LOWTIER_MULT: Readonly<Record<StyleKey, number>> = {
  rock: 0.90, tag: 0.95, lag: 1.05, station: 1.25, maniac: 0.70,
};

/** 听牌面对下注的底池赔率因子：低压（赔率合适）给足、高压（赔率不足）快速衰减。 */
function drawPressureFactor(pressure: number): number {
  return Math.max(0, Math.min(1, 1.274 - 0.487 * pressure)); // p=0.85→0.86, p=1→0.79, p=2→0.30
}
/** 弱/边缘牌无听牌的压力因子：pressure≥1 迅速趋零（理智弃牌）。 */
function lowTierPressureFactor(pressure: number): number {
  return Math.max(0, Math.min(1, 0.66 - 0.42 * pressure)); // p=1→0.24, p=2→0
}

/** 情绪状态（PRD 2.1 ②）。 */
export type Emotion = 'calm' | 'tilted' | 'cautious';

export const EMOTION_NAMES_CN: Readonly<Record<Emotion, string>> = {
  calm: '平稳',
  tilted: '上头（变松）',
  cautious: '谨慎（变紧）',
};

/** AI 的持久状态（跨手保留），存档时需要序列化。 */
export interface AiState {
  readonly seat: number;
  readonly style: StyleKey;
  emotion: Emotion;
  /** 情绪剩余手数，归零后回到 calm。 */
  emotionHandsLeft: number;
  /** 连续输掉大池的次数，达到阈值触发情绪。 */
  consecutiveBigLosses: number;
}

/** 创建 AI 初始状态。 */
export function createAiState(seat: number, style: StyleKey): AiState {
  return { seat, style, emotion: 'calm', emotionHandsLeft: 0, consecutiveBigLosses: 0 };
}

/** 位置分类（PRD 2.1 决策输入之一）。 */
export type Position = 'early' | 'middle' | 'late' | 'blinds';

/** 决策请求 —— 注意：这里刻意**没有**任何未开牌 / 对手底牌字段。 */
export interface DecisionInput {
  /** 我的两张底牌。 */
  readonly hole: readonly [Card, Card];
  /** 已公开的公共牌（0 / 3 / 4 / 5 张）。 */
  readonly board: readonly Card[];
  /** 当前底池总额（含本街已投入）。 */
  readonly pot: number;
  /** 我剩余筹码。 */
  readonly stack: number;
  /** 我本街已投入。 */
  readonly streetContributed: number;
  /** 引擎给出的合法动作集合。AI 只能在其中挑选。 */
  readonly legal: LegalActions;
  /** 大盲金额，用于把下注尺度换算成筹码。 */
  readonly bigBlind: number;
  /** 本街当前最高注额（to 语义），用于计算反加尺度。 */
  readonly currentBet: number;
  /** 位置。 */
  readonly position: Position;
  /** 本手仍未弃牌的玩家数（含我）。 */
  readonly activePlayers: number;
  /** 本街是否已有人加注（翻牌前用于区分开池 / 面对加注）。 */
  readonly facingRaise: boolean;
  /** 本手我之前的动作序列（PRD 2.1「本手历史动作」）。 */
  readonly myPriorActions: readonly string[];
}

/** 决策日志（PRD AC2.6：含风格参数、牌力分档、随机扰动值、情绪状态与最终动作）。 */
export interface DecisionLog {
  readonly seat: number;
  readonly style: StyleKey;
  readonly styleNameCn: string;
  readonly street: 'preflop' | 'flop' | 'turn' | 'river';
  readonly tier: StrengthTier;
  readonly tierNameCn: string;
  /** 模糊牌力标量（Chen 百分位 / 结构估分），非赢率。 */
  readonly strengthScore: number;
  readonly strengthReason: string;
  readonly emotion: Emotion;
  readonly emotionNameCn: string;
  /** 本次决策用到的随机扰动值（阈值偏移量）。 */
  readonly jitter: number;
  /** 本次决策抽到的主随机数 ∈ [0,1)。 */
  readonly roll: number;
  /** 是否触发了「概率化犯错」。 */
  readonly mistake: boolean;
  readonly position: Position;
  /** 跟注额 / 底池比，即下注压力。 */
  readonly pressure: number;
  readonly action: Action;
  /** 人类可读的决策理由。 */
  readonly reason: string;
  /** 拟人思考停顿（ms，1x 速度）。 */
  readonly thinkMs: number;
}

/** 决策结果。 */
export interface Decision {
  readonly action: Action;
  readonly log: DecisionLog;
}

/** 情绪触发阈值：单手净损失超过起始筹码的这个比例，记为「输了个大池」。 */
const BIG_LOSS_RATIO = 0.25;
/** 连输几个大池后触发情绪波动。 */
const BIG_LOSS_TRIGGER = 2;

/**
 * 每手结束后更新情绪（PRD 2.1 ②）。
 *
 * @param netChips 本手净收益（负数为亏损）
 * @param stackRef 用于换算「大池」的参考筹码量（一般用起始带入筹码）
 * @param rng      aiRNG（绝不能传 shuffleRNG）
 */
export function updateEmotion(state: AiState, netChips: number, stackRef: number, rng: Rng): void {
  if (state.emotionHandsLeft > 0) {
    state.emotionHandsLeft -= 1;
    if (state.emotionHandsLeft === 0) state.emotion = 'calm';
  }

  const bigLoss = netChips < 0 && Math.abs(netChips) >= stackRef * BIG_LOSS_RATIO;
  if (bigLoss) {
    state.consecutiveBigLosses += 1;
    if (state.consecutiveBigLosses >= BIG_LOSS_TRIGGER && state.emotion === 'calm') {
      // 一半人上头变松、一半人受挫变紧 —— 真人两种反应都常见
      state.emotion = rng.next() < 0.5 ? 'tilted' : 'cautious';
      // 持续 3–10 手后回归（PRD 2.1）
      state.emotionHandsLeft = 3 + rng.nextInt(8);
      state.consecutiveBigLosses = 0;
    }
  } else if (netChips > 0) {
    state.consecutiveBigLosses = 0;
  }
}

/** 情绪对牌力阈值的偏移：tilted 更松（阈值下调），cautious 更紧（阈值上调）。 */
function emotionShift(emotion: Emotion): number {
  switch (emotion) {
    case 'tilted': return -0.06;
    case 'cautious': return 0.05;
    default: return 0;
  }
}

/** 位置对阈值的偏移：后位更松，前位更紧。 */
function positionShift(pos: Position): number {
  switch (pos) {
    case 'early': return 0.035;
    case 'middle': return 0.01;
    case 'late': return -0.03;
    case 'blinds': return -0.01;
    default: return 0;
  }
}

/** 拟人思考停顿：0.8–2.5s（1x），非固定时长（PRD 2.1 ④ / AC2.4）。 */
export function thinkTimeMs(rng: Rng, speed = 1): number {
  const base = rng.nextFloat(800, 2500); // 0.8–2.5s，非固定时长
  return base / speed;
}

/** 把「占底池比例」的下注意图夹到合法区间内，并取整。 */
function sizeBet(input: DecisionInput, ratio: number, min: number, max: number): number {
  const target = input.streetContributed + Math.round(input.pot * ratio);
  return Math.max(min, Math.min(max, Math.max(min, target)));
}

/**
 * 核心决策函数。
 *
 * @param rng 必须是 aiRNG（PRD 8.4）。传入 shuffleRNG 会污染发牌序列，破坏可复现契约。
 */
export function decide(state: AiState, input: DecisionInput, rng: Rng, speed = 1): Decision {
  const style = getStyle(state.style);
  const strength = fuzzyStrength(input.hole, input.board);
  const street: DecisionLog['street'] =
    input.board.length === 0 ? 'preflop' : input.board.length === 3 ? 'flop' : input.board.length === 4 ? 'turn' : 'river';

  // ---- 随机扰动：每次决策都重新抽，保证 AC2.3（同局面多次决策不完全相同）----
  const jitter = (rng.next() * 2 - 1) * style.jitter;
  const roll = rng.next();
  const mistakeRoll = rng.next();
  const thinkMs = thinkTimeMs(rng, speed);

  const pressure = input.pot > 0 ? input.legal.toCall / input.pot : 0;
  const shift = jitter + emotionShift(state.emotion) + positionShift(input.position);

  let action: Action;
  let reason: string;

  if (street === 'preflop') {
    ({ action, reason } = decidePreflop(style, strength, input, shift, roll));
  } else {
    ({ action, reason } = decidePostflop(style, strength, input, shift, roll, pressure));
  }

  // ---- 概率化犯错（PRD 2.1 ③）：小概率把决策翻向"明显不利"的方向 ----
  let mistake = false;
  if (mistakeRoll < style.mistakeRate) {
    const flipped = flipToMistake(action, input, strength.tier);
    if (flipped) {
      mistake = true;
      action = flipped.action;
      reason = `${reason}｜触发概率化犯错：${flipped.note}`;
    }
  }

  action = clampToLegal(action, input.legal);

  return {
    action,
    log: {
      seat: state.seat, style: state.style, styleNameCn: style.nameCn, street,
      tier: strength.tier, tierNameCn: TIER_NAMES_CN[strength.tier],
      strengthScore: strength.score, strengthReason: strength.reason,
      emotion: state.emotion, emotionNameCn: EMOTION_NAMES_CN[state.emotion],
      jitter, roll, mistake, position: input.position, pressure, action, reason, thinkMs,
    },
  };
}

/* ------------------------------------------------------------------ *
 * 翻牌前
 * ------------------------------------------------------------------ */

function decidePreflop(
  style: Style,
  strength: FuzzyStrength,
  input: DecisionInput,
  shift: number,
  roll: number,
): { action: Action; reason: string } {
  const p = strength.score; // Chen 百分位
  const { legal } = input;

  if (!input.facingRaise) {
    // 无人加注：开池 / 跛入 / 弃牌
    const raiseAt = style.openRaiseAt + shift;
    const limpAt = style.limpAt + shift;

    if (p >= raiseAt && (legal.raise || legal.bet)) {
      const to = openRaiseSize(input, roll);
      if (legal.raise && to >= legal.raise.min) {
        return { action: { type: 'raise', amount: Math.min(to, legal.raise.max) }, reason: `开池加注（百分位 ${(p * 100).toFixed(0)}% ≥ 阈值 ${(raiseAt * 100).toFixed(0)}%）` };
      }
      if (legal.bet && to >= legal.bet.min) {
        return { action: { type: 'bet', amount: Math.min(to, legal.bet.max) }, reason: '开池下注' };
      }
    }
    if (p >= limpAt) {
      // Round23 · BUG-R01（AC-5.5）：诈唬型「入池即加注」——按 openLimpToRaise 概率把跛入升级为
      // 开池加注。只作用于本可跛入的手（limpAt ≤ p < raiseAt），不改 VPIP（跛入/加注同属自愿入池），
      // 复用已抽取的 roll（不新增 rng.next()，其余 persona openLimpToRaise=0 时行为逐字节不变）。
      if (roll < style.openLimpToRaise && (legal.raise || legal.bet)) {
        const to = openRaiseSize(input, roll);
        if (legal.raise && to >= legal.raise.min) {
          return { action: { type: 'raise', amount: Math.min(to, legal.raise.max) }, reason: `诈唬型入池加注（百分位 ${(p * 100).toFixed(0)}%，跛入升级）` };
        }
        if (legal.bet && to >= legal.bet.min) {
          return { action: { type: 'bet', amount: Math.min(to, legal.bet.max) }, reason: '诈唬型入池下注（跛入升级）' };
        }
      }
      if (legal.call) return { action: { type: 'call' }, reason: `跛入跟注（百分位 ${(p * 100).toFixed(0)}% ≥ 阈值 ${(limpAt * 100).toFixed(0)}%）` };
      if (legal.check) return { action: { type: 'check' }, reason: '免费看牌，过牌' };
    }
    if (legal.check) return { action: { type: 'check' }, reason: '牌力不足但可免费过牌（不计入 VPIP）' };
    return { action: { type: 'fold' }, reason: `牌力不足弃牌（百分位 ${(p * 100).toFixed(0)}% < 阈值 ${(limpAt * 100).toFixed(0)}%）` };
  }

  // 面对加注：反加 / 跟注 / 弃牌
  // ── Round22 · F4 翻前差牌过激治理（rootcause §问题4 A/B/C/D 四个叠加根因）──
  //   R4.1 加注深度阶梯阈值：按 3-bet / 4-bet / 5-bet+ 取逐级抬高的反加阈值；
  //   R4.2 反加倍率阶梯衰减：3-bet ×3.0 → 4-bet ×2.2 → 5-bet+ 仅强牌小幅加注，否则退跟注；
  //   R4.3 preflop pressure 因子：反加/跟注阈值随「面对注额/底池」单调抬高（注额越大越谨慎）；
  //   R4.5 preflop all-in 护栏：非短码且 tier<strong 的主动大额投入一律降级（见 violatesAllInGuard）。
  const depth = preflopRaiseDepth(input);
  const depthBase = depth === 3 ? style.threeBetAt : depth === 4 ? style.fourBetAt : style.fiveBetAtPlus;
  const facingRate = input.pot > 0 ? input.legal.toCall / input.pot : 0;
  const pressure = PREFLOP_PRESSURE_K * facingRate;
  const reRaiseAt = depthBase + shift + pressure;
  const callAt = style.callRaiseAt + shift + pressure - (style.callAppetite - 1) * 0.05;
  const madeStrong = strength.tier === 'strong' || strength.tier === 'monster';
  const depthLabel = depth === 3 ? '3bet' : depth === 4 ? '4bet' : '5bet+';

  // 5-bet 及更深：非强牌（tier<strong）不再反加，只跟/弃（R4.2 尾段）。
  if (p >= reRaiseAt && legal.raise && (depth < 5 || madeStrong)) {
    const to = Math.min(legal.raise.max, Math.max(legal.raise.min, Math.round(input.currentBet * reRaiseMult(depth))));
    if (!violatesAllInGuard(input, strength.tier, to)) {
      return {
        action: { type: 'raise', amount: to },
        reason: `反加 ${depthLabel}（百分位 ${(p * 100).toFixed(0)}% ≥ 阈值 ${(reRaiseAt * 100).toFixed(0)}%，压力 ${(facingRate * 100).toFixed(0)}% 池）`,
      };
    }
    // 命中 all-in 护栏：差牌不主动推全下，落到跟注 / 弃牌分支。
  }
  if (p >= callAt && legal.call) {
    return { action: { type: 'call' }, reason: `跟注加注（百分位 ${(p * 100).toFixed(0)}% ≥ 阈值 ${(callAt * 100).toFixed(0)}%，压力 ${(facingRate * 100).toFixed(0)}% 池）` };
  }
  if (legal.check) return { action: { type: 'check' }, reason: '无需补注，过牌' };
  return { action: { type: 'fold' }, reason: `面对加注牌力不足，弃牌（百分位 ${(p * 100).toFixed(0)}%，压力 ${(facingRate * 100).toFixed(0)}% 池）` };
}

/* ------------------------------------------------------------------ *
 * Round22 · F4 翻前决策辅助（加注深度 / 压力 / all-in 护栏）
 * ------------------------------------------------------------------ */

/** preflop 下注压力系数：反加/跟注阈值 += K × (面对注额 / 底池)。注额越大要求牌力越高（R4.3 / AC4.3）。 */
const PREFLOP_PRESSURE_K = 0.25;
/** 短码阈值：有效筹码 < 10BB 视为短码，此时 all-in 属合理策略，护栏放行（R4.5 边界 / AC4.7）。 */
const SHORT_STACK_BB = 10;

/**
 * 由「当前最高注额 / 大盲」推断翻前加注深度（3-bet / 4-bet / 5-bet+）。
 *   - 面对开池加注（约 2.2–3.4BB）→ 我的再加注是 **3-bet**；
 *   - 面对 3-bet（约 7–11BB）→ **4-bet**；
 *   - 面对 4-bet 及更深 → **5-bet+**。
 * 不依赖任何被隔离的信息（AC2.5），只用引擎已下发的 currentBet 与 bigBlind。
 */
function preflopRaiseDepth(input: DecisionInput): 3 | 4 | 5 {
  const betBB = input.bigBlind > 0 ? input.currentBet / input.bigBlind : 0;
  if (betBB <= 4.5) return 3;
  if (betBB <= 13) return 4;
  return 5;
}

/** 反加倍率阶梯（R4.2）：3-bet ×3.0、4-bet ×2.2、5-bet+ 强牌 ×2.0（取代旧的恒定 ×2.7）。 */
function reRaiseMult(depth: 3 | 4 | 5): number {
  return depth === 3 ? 3.0 : depth === 4 ? 2.2 : 2.0;
}

/**
 * Round22 · F4（R4.5 / AC4.7）Preflop all-in 护栏。
 *
 * 当**非短码**（有效筹码 ≥10BB）且手牌 tier < strong（弱/边缘/中等）时，
 * 若某个「主动投入」动作要额外投入的筹码超过本方剩余筹码的 30%，返回 true 表示越界 —— 调用方
 * 据此把该主动加注降级为跟注/弃牌，禁止差牌主动推全下。
 *
 * 边界：
 *   - 短码（<10BB）放行：短码 all-in 是合理策略（AC4.7 边界 / 风险 R2）。
 *   - strong / monster 放行：不误伤强牌价值全下（AC4.7 语义）。
 */
function violatesAllInGuard(input: DecisionInput, tier: StrengthTier, commitTo: number): boolean {
  const isShort = input.bigBlind > 0 && input.stack / input.bigBlind < SHORT_STACK_BB;
  if (isShort) return false;
  if (tier === 'strong' || tier === 'monster') return false;
  const additionalCommit = commitTo - input.streetContributed;
  return additionalCommit > 0.30 * input.stack;
}

/** 开池加注尺度：2.2–3.4 倍大盲（含跟注额口径，用 to 语义）。 */
function openRaiseSize(input: DecisionInput, roll: number): number {
  const mult = 2.2 + roll * 1.2;
  return Math.round(input.bigBlind * mult);
}

/* ------------------------------------------------------------------ *
 * Round23 · FR-5 翻牌后重设计辅助（权益折算 / 赔率 / 大额护栏 / 概率化诈唬）
 * ------------------------------------------------------------------ */

/** 数值夹取。 */
function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}

/**
 * Round23 · AC-5.2 纯空气诈唬 persona 收敛因子（情境因子，PRD §问题5「诈唬 = 基础诈唬率 × 情境因子」）。
 *
 * 背景：AC-5.2 要求「纯诈唬频率＝空气牌主动下注/加注 ÷ 全部主动下注」落 persona 带——
 *   rock 2–7%、station 1–5%（被动/保守型真人极少拿纯空气牌主动开火，其主动下注绝大多数是价值下注）。
 * 保守/跟注站这两类因价值下注旋钮偏低（betStrong/betMedium 小、且被 round6-ladder 静态锁定不可改），
 * 分母天然小，纯诈唬占比被抬高。R12 的 `bluffFreq` 出厂值同样被静态锁定，故这里用**情境因子**单独
 * 收敛这两类 persona 的**纯空气**（无听牌）主动发起频率；半诈唬（听牌，走 `bluffFreq×1.3/0.8` 路径，
 * 权益≥25% 不计入纯诈唬口径）与其余三类 persona 一律不受影响（守住 S5「半诈唬≥纯空气」「lag>tag」
 * 「maniac 最高」的结构序）。
 */
const PURE_AIR_BLUFF_FACTOR: Readonly<Record<StyleKey, number>> = {
  tag: 1, lag: 1, maniac: 1, rock: 0.30, station: 0.45,
};

/**
 * 多人底池胜率折算（AC-5：赔率与门槛判定的单挑胜率按在场对手数下折）。
 * 单挑（1 对手）不变；对手越多，胜率按幂次衰减（保守近似，非精算）。
 */
function multiwayEquity(headsUp: number, activePlayers: number): number {
  const opp = Math.max(1, activePlayers - 1);
  if (opp <= 1) return headsUp;
  return clamp01(headsUp ** (1 + 0.30 * (opp - 1)));
}

/**
 * Round23 · FR-5（AC-5.1 / AC-5.3）Postflop 空气 / 边缘牌大额 · 全下护栏。
 *
 * 把 Round22 preflop 的「非短码差牌禁止大额投入」延伸到 postflop：**仅**约束低权益的
 * 空气 / 边缘牌（tier=weak/marginal），主动下注 / 加注的额外投入受权益分级封顶——空气最紧、
 * 边缘次之。成手（medium/strong/monster）与短码放行（价值 / 保护性下注、短码全下均合理）。
 * 返回**允许的最大 to 值**；`null` 表示不设限。
 */
function postflopSizeCap(input: DecisionInput, tier: StrengthTier, equity: number): number | null {
  const isShort = input.bigBlind > 0 && input.stack / input.bigBlind < SHORT_STACK_BB;
  if (isShort) return null;
  if (tier !== 'weak' && tier !== 'marginal') return null; // 只约束空气 / 边缘牌
  // 权益分级封顶比例：目标是**杜绝空气主动全下**（AC-5.1）而非扼杀所有诈唬进攻——
  // 因此封顶取「明显小于全栈」的比例，既阻断 all-in（需≈100% 栈），又保留半诈唬 / 施压加注的空间
  // （维持 AC2.2.4 / AC-5.5 的 AF 结构序，诈唬型 AF 由 openLimpToRaise 抬到全 persona 最高）。空气(<0.25) 50%、极弱(<0.35) 60%、边缘 70%。
  const capFrac = equity < 0.25 ? 0.50 : equity < 0.35 ? 0.60 : 0.70;
  return input.streetContributed + Math.floor(capFrac * input.stack);
}

function decidePostflop(
  style: Style,
  strength: FuzzyStrength,
  input: DecisionInput,
  shift: number,
  roll: number,
  pressure: number,
): { action: Action; reason: string } {
  const { legal } = input;
  const tier = strength.tier;
  const madeHand = tier === 'monster' || tier === 'strong';
  const styleKey = style.key;
  // 情绪 / 位置偏移在翻牌后折算成"下注意愿"的加减
  const willingness = -shift * 2;
  // 多人底池胜率折算（赔率 / 门槛判定用；tier 分档仍是单挑结构估分）。
  const eqMw = multiwayEquity(strength.equity, input.activePlayers);

  const sizingRatio = style.betSizing[0] + roll * (style.betSizing[1] - style.betSizing[0]);
  const TIER_CN = TIER_NAMES_CN[tier];

  if (legal.toCall === 0) {
    // 我可以过牌，也可以主动下注
    let betProb: number;
    let bluffing = false;
    let pureAir = false;
    if (madeHand) betProb = style.betStrong;
    else if (tier === 'medium') betProb = style.betMedium;
    else {
      // 空气 / 边缘：概率化诈唬 = persona 基础诈唬率 × 情境因子（AC-5.2；R12 §4.2.2 S5 频率带）。
      //   半诈唬（有听牌）1.3 倍加成；纯空气（无听牌）主动下注频率整体乘 persona 收敛因子
      //   （rock/station 收敛，其余为 1）——非每手必诈，persona 梯度保留。
      bluffing = true;
      betProb = strength.hasDraw ? style.bluffFreq * 1.3 : style.bluffFreq;
      pureAir = !strength.hasDraw;
    }
    betProb = clamp01(betProb + willingness);
    // 纯空气收敛作用在**含情绪/位置扰动后的**总下注概率上（否则被动型的 willingness 会绕过收敛，
    // 把纯诈唬频率重新抬高）；只作用于纯空气，价值/半诈唬不受影响。
    if (pureAir) betProb = clamp01(betProb * PURE_AIR_BLUFF_FACTOR[styleKey]);

    if (roll < betProb) {
      const cap = postflopSizeCap(input, tier, strength.equity);
      // 主动下注
      if (legal.bet) {
        let to = sizeBet(input, sizingRatio, legal.bet.min, legal.bet.max);
        if (cap !== null) {
          if (legal.bet.min > cap) {
            // 连最小下注都会突破护栏（会被迫大额/全下）→ 差牌不推，过牌控池（AC-5.1）。
            if (legal.check) return { action: { type: 'check' }, reason: `过牌（${TIER_CN}，大额护栏拦截空气下注）` };
          } else {
            to = Math.min(to, cap);
            const kind = bluffing ? '诈唬下注' : '价值下注';
            return { action: { type: 'bet', amount: to }, reason: `${kind}（${TIER_CN}，尺度 ${(sizingRatio * 100).toFixed(0)}% 池，护栏封顶）` };
          }
        } else {
          const kind = tier === 'weak' || tier === 'marginal' ? '诈唬下注' : '价值下注';
          return { action: { type: 'bet', amount: to }, reason: `${kind}（${TIER_CN}，尺度 ${(sizingRatio * 100).toFixed(0)}% 池）` };
        }
      } else if (legal.raise) {
        let to = sizeBet(input, sizingRatio, legal.raise.min, legal.raise.max);
        if (cap !== null && legal.raise.min > cap) {
          if (legal.check) return { action: { type: 'check' }, reason: `过牌（${TIER_CN}，大额护栏拦截空气加注）` };
        } else {
          if (cap !== null) to = Math.min(to, cap);
          return { action: { type: 'raise', amount: to }, reason: `主动加注（${TIER_CN}）` };
        }
      }
    }
    if (legal.check) return { action: { type: 'check' }, reason: `过牌（${TIER_CN}，本次未触发下注）` };
    return { action: { type: 'fold' }, reason: '无可用动作，弃牌' };
  }

  // 面对下注：加注 / 跟注 / 弃牌
  // ---- 赔率驱动（AC-5.4）：所需胜率 = 底池赔率 − persona 偏移；胜率不足的低权益烂牌默认弃 ----
  const potOdds = input.pot + legal.toCall > 0 ? legal.toCall / (input.pot + legal.toCall) : 0;
  const requiredEq = potOdds - style.oddsBias;
  const oddsMet = eqMw >= requiredEq;

  // 加注（价值 / 诈唬加注）
  let raiseProb: number;
  const pureAirRaise = !madeHand && tier !== 'medium' && !strength.hasDraw;
  if (tier === 'monster') raiseProb = style.raiseStrong * 1.15;
  else if (tier === 'strong') raiseProb = style.raiseStrong;
  else if (tier === 'medium') raiseProb = style.raiseMedium;
  else raiseProb = strength.hasDraw ? style.bluffFreq * 0.8 : style.bluffFreq * 0.25;
  raiseProb = clamp01(raiseProb + willingness);
  // 纯空气诈唬加注同样整体收敛（含 willingness 扰动），口径与主动下注一致（AC-5.2）。
  if (pureAirRaise) raiseProb = clamp01(raiseProb * PURE_AIR_BLUFF_FACTOR[styleKey]);

  let raiseBlocked = false;
  if (roll < raiseProb && legal.raise) {
    let to = sizeBet(input, Math.max(sizingRatio, 0.75), legal.raise.min, legal.raise.max);
    const cap = postflopSizeCap(input, tier, strength.equity);
    if (cap !== null && legal.raise.min > cap) {
      // 空气 / 边缘牌想诈唬加注、但最小加注就突破护栏（会被迫大额 / 全下）→ 放弃激进线（AC-5.1）。
      raiseBlocked = true;
    } else {
      if (cap !== null) to = Math.min(to, cap);
      return { action: { type: 'raise', amount: to }, reason: `加注施压（${TIER_CN}）` };
    }
  }

  // 跟注意愿：按牌力档位选取翻后专用跟注模型（R12-2 再平衡；与翻前 callAppetite 解耦）。
  // pressure 是 PRD 2.1 明确允许的「跟注额 / 底池比」输入；equity 为结构启发式近似（AC2.5 未破隔离）。
  const tierCallBase: Record<StrengthTier, number> = {
    monster: 0.99, strong: 0.92, medium: 0.62, marginal: 0.30, weak: 0.08,
  };
  let callProb: number;
  if (madeHand) {
    // 成手牌：价值跟注，对压力钝感（封顶 0.30 的轻惩罚）。
    callProb = tierCallBase[tier] * MADE_MULT[styleKey] - Math.min(0.30, pressure * 0.10);
  } else if (strength.hasDraw) {
    // 听牌：按底池赔率跟注（S4）——低压给足、高压（赔率不足）快速衰减。
    callProb = DRAW_CALL[styleKey] * drawPressureFactor(pressure);
  } else if (tier === 'medium') {
    // 中等成手牌（S1/S2）：pressure=1 落靶心，随下注尺度线性递减（保证 S2<S1）。
    callProb = MED_CALL_1X[styleKey] - MED_BITE[styleKey] * pressure;
  } else {
    // 弱/边缘牌无听牌（S3）：理智弃牌——appetite 低位 × 压力因子在 pressure≥1 时迅速趋零。
    callProb = tierCallBase[tier] * LOWTIER_MULT[styleKey] * lowTierPressureFactor(pressure);
  }
  // Round23 · 赔率门（AC-5.3 / AC-5.4）：**仅**低档烂牌（weak/marginal 且无听牌）在估计胜率
  // 达不到底池赔率时跟注意愿强衰减（收敛「烂牌全跟」）；medium 成手/听牌不受影响（守 R12 S1/S2/S4 手感）。
  const isTrash = tier === 'weak' || tier === 'marginal';
  if (isTrash && !strength.hasDraw && !oddsMet) {
    callProb *= 0.15;
  }
  callProb = clamp01(callProb + willingness);

  // R11-3 根因B：仅当加注确实合法时，未触发的加注概率才回落成跟注。
  // Round23：若加注被空气护栏拦截（raiseBlocked），则该 roll 不自动计入跟注——空气放弃激进后
  // 仅按自身 callProb 独立判定（低权益烂牌 callProb 已被赔率门压到极低 → 弃牌，不"被迫跟大注"）。
  const callThreshold = raiseBlocked
    ? callProb
    : legal.raise
      ? raiseProb + callProb
      : callProb + (madeHand ? raiseProb : 0);

  if (roll < callThreshold && legal.call) {
    return { action: { type: 'call' }, reason: `跟注（${TIER_CN}，压力 ${(pressure * 100).toFixed(0)}% 池，胜率≈${(eqMw * 100).toFixed(0)}% vs 赔率 ${(potOdds * 100).toFixed(0)}%）` };
  }
  if (legal.check) return { action: { type: 'check' }, reason: '过牌' };
  return { action: { type: 'fold' }, reason: `弃牌（${TIER_CN}，压力 ${(pressure * 100).toFixed(0)}% 池，胜率≈${(eqMw * 100).toFixed(0)}% < 赔率 ${(potOdds * 100).toFixed(0)}%）` };
}

/* ------------------------------------------------------------------ *
 * 概率化犯错与合法性收敛
 * ------------------------------------------------------------------ */

/**
 * 把决策翻向"明显不利"的方向（PRD 2.1 ③）：
 *  - 本该弃牌 → 改为跟注（跟注明显不利的牌）
 *  - 本该跟注/加注强牌 → 改为弃牌（弃掉不该弃的牌）
 */
function flipToMistake(
  action: Action,
  input: DecisionInput,
  tier: StrengthTier,
): { action: Action; note: string } | null {
  const { legal } = input;
  if (action.type === 'fold' && legal.call) {
    // Round22 · F4（AC4.3）：翻前面对大额再加注（≥8BB，多为大 3-bet / 4-bet / 5-bet+）持差牌时，
    // 「松手误跟」不成立——真人的概率化失误是轻注好奇跟注，不会拿废牌跟进明显的大额再加注。
    // 此处不制造该失误（其余局面照常翻牌 fold→call），既守 AC4.3 上限又不削弱整体 mistakeRate（AC2.3）。
    const bb = input.bigBlind;
    const bigReraisePreflop = input.board.length === 0 && bb > 0 && input.currentBet >= 8 * bb;
    const trash = tier === 'weak' || tier === 'marginal';
    if (bigReraisePreflop && trash) return null;
    return { action: { type: 'call' }, note: '用不该跟的牌跟注' };
  }
  if ((action.type === 'call' || action.type === 'raise') && (tier === 'strong' || tier === 'monster') && legal.fold && legal.toCall > 0) {
    return { action: { type: 'fold' }, note: '弃掉了不该弃的强牌' };
  }
  return null;
}

/**
 * 最后一道保险：把动作收敛到合法集合内。
 *
 * AI 的启发式在极端筹码 / 极端注额下可能产出越界金额；引擎会直接拒绝并抛错，
 * 那会让整局中断。这里做一次"就近合法化"，把越界金额夹回区间，
 * 并在完全无法执行时降级为 check / fold。
 * 注意：这是**防御**，不是掩盖 —— 单测 aiLegality 会断言 AI 在 10 万次决策里
 * 产出的原始动作本身就全部合法（即这道保险不应被触发）。
 */
export function clampToLegal(action: Action, legal: LegalActions): Action {
  switch (action.type) {
    case 'fold':
      return legal.fold ? action : legal.check ? { type: 'check' } : action;
    case 'check':
      return legal.check ? action : legal.call ? { type: 'call' } : { type: 'fold' };
    case 'call':
      return legal.call ? action : legal.check ? { type: 'check' } : { type: 'fold' };
    case 'bet': {
      if (!legal.bet) return legal.check ? { type: 'check' } : { type: 'fold' };
      const amt = Math.max(legal.bet.min, Math.min(legal.bet.max, action.amount ?? legal.bet.min));
      return { type: 'bet', amount: amt };
    }
    case 'raise': {
      if (!legal.raise) {
        if (legal.call) return { type: 'call' };
        return legal.check ? { type: 'check' } : { type: 'fold' };
      }
      const amt = Math.max(legal.raise.min, Math.min(legal.raise.max, action.amount ?? legal.raise.min));
      return { type: 'raise', amount: amt };
    }
    case 'allin':
      return legal.allIn ? action : legal.check ? { type: 'check' } : { type: 'fold' };
    default:
      return { type: 'fold' };
  }
}
